/**
 * knox-config.tsx — shared configuration context for Knox UI.
 *
 * Authentication is handled via HttpOnly cookies (knox_token),
 * so no auth token is stored in localStorage or exposed to JS.
 */

import { createContext, useContext, type ReactNode } from 'react';

export interface KnoxConfig {
  elevoApiBase: string;
  gatewayUrl: string;
  /** Gating for text body file path auto-detection (knox-file:// links in markdown).
   *  Does NOT gate structured resource_items cards from agent present_result —
   *  those are always clickable. */
  enableFilePathLinks: boolean;
  /** Base URL for secure HTML preview service (separate domain/port for XSS isolation) */
  previewBaseUrl: string;
}

const KnoxConfigContext = createContext<KnoxConfig>({
  elevoApiBase: '',
  gatewayUrl: '',
  enableFilePathLinks: false,
  previewBaseUrl: '',
});

export function KnoxConfigProvider({ children }: { children: ReactNode }) {
  const config: KnoxConfig = {
    elevoApiBase: import.meta.env.VITE_ELEVO_API_BASE || '',
    gatewayUrl: import.meta.env.VITE_GATEWAY_URL || '',
    enableFilePathLinks: import.meta.env.VITE_ENABLE_FILE_PATH_LINKS === 'true',
    previewBaseUrl: import.meta.env.VITE_PREVIEW_BASE_URL || 'http://localhost:3041',
  };

  return (
    <KnoxConfigContext.Provider value={config}>
      {children}
    </KnoxConfigContext.Provider>
  );
}

export function useKnoxConfig(): KnoxConfig {
  return useContext(KnoxConfigContext);
}
